'use client';

import { isGuestUser } from '@ps/functions/auth/isGuestUser';
import { markSessionNone } from '@ps/functions/auth/markSessionNone';
import { postSessionCookie } from '@ps/functions/auth/postSessionCookie';
import type { User } from '@ps/functions/firebase/auth';

export interface WarmSessionCookieOptions {
  /**
   * Whether a signed-in GUEST account is a legitimate actor on this surface, so the
   * app-start guest cleanup below must leave it alone. Only the checkout tree sets it —
   * see the cleanup comment for the redirect-payment round trip it exists for.
   */
  keepGuestSession: boolean;
  /**
   * Whether a signed-in user with NO profile yet is a legitimate actor on this
   * surface. Only the signup surfaces set it: a creator is signed in long before
   * the funnel writes their profile, and the social path returns from Meta as a
   * fresh document load.
   */
  keepProfilelessSession: boolean;
}

/**
 * `false` only when the read SUCCEEDED and found no doc. A failed read resolves
 * `true`: signing a real user out over a Firestore hiccup is far worse than
 * minting one cookie for an account that turns out to be incomplete.
 */
async function hasProfile(uid: string): Promise<boolean> {
  try {
    const { doc, firestore, getDoc } =
      await import('@ps/functions/firebase/firestore');
    const snapshot = await getDoc(doc(firestore(), 'profiles', uid));
    return snapshot.exists();
  } catch (error) {
    console.error('[warmSessionCookie] profile check failed:', error);
    return true;
  }
}

/**
 * Reconcile the server-side `__session` cookie with the client's resolved Firebase
 * auth state ONCE per app load (at app start), so the cookie is present before the
 * visitor can open an intercepted modal (the checkout modal's server layout runs
 * `trySessionRefresh`, which tears the modal down only when the cookie is `absent`):
 *   - signed in (real) → POST the ID token → server mints `s:<jwt>`
 *   - signed out       → POST `/auth/session/none` → server writes the `n:1` marker
 *   - lingering guest  → `logout()` → cleared, ending as `n:1`
 *   - signed in, no profile (abandoned signup) → `logout()`, ending as `n:1`
 *     (unless `keepProfilelessSession` — the signup surfaces)
 *     (unless `keepGuestSession` — then the guest stays signed in and we only write `n:1`)
 *
 * **Runs exactly once per app lifecycle.** Several `SessionWarmer`s can mount (each
 * `AuthProvider` in the tree renders one) and the auth state changes during a session
 * — notably the checkout engine mints a throwaway guest IN PLACE on a bio page (the
 * express Apple Pay button). A module-scope `warmed` guard makes only the FIRST
 * definite auth state (app start) reconcile the cookie; every later call — a
 * duplicate `SessionWarmer`, a mid-session guest mint — is a no-op. That guard is why
 * there is no `/checkout` pathname check anymore: warming never fires mid-session, so
 * it can't log a just-minted guest back out. It does NOT cover a guest checkout that
 * spans two app loads (a redirect payment) — that's what `keepGuestSession` is for.
 * Login / logout flows post their own session
 * cookie (`@login` content, `useOAuth2Login`, `logout` → `markSessionNone`), so
 * dropping the reactive re-warm doesn't lose mid-session transitions.
 *
 * Firebase-free at the STATIC import level (`User` is a type-only import; `logout` —
 * which pulls the SDK — is dynamic-imported only on the guest path).
 */
let warmed = false;

export async function warmSessionCookie(
  user: User | null,
  { keepGuestSession, keepProfilelessSession }: WarmSessionCookieOptions,
): Promise<void> {
  if (warmed) {
    return;
  }
  warmed = true;

  if (!user) {
    try {
      await markSessionNone();
    } catch (error) {
      console.error('[warmSessionCookie] marking logged-out failed:', error);
    }
    return;
  }

  // A failed claim read must not be mistaken for "not a guest" silently, but it also
  // must not block the signed-in path below — a real user whose claims didn't load
  // still needs their session cookie.
  let isGuest = false;
  try {
    isGuest = await isGuestUser(user);
  } catch (error) {
    console.error('[warmSessionCookie] reading the guest claim failed:', error);
  }

  if (isGuest) {
    // A lingering guest account (minted by a PREVIOUS session's guest checkout) is
    // logged OUT at app start so the throwaway guest doesn't stick around as the
    // current user (the bio header would otherwise read it as "logged in").
    //
    // `keepGuestSession` surfaces the ONE case where an app start is really the
    // continuation of a guest checkout, so the guest is NOT lingering: a redirect
    // payment (3DS, or any redirect-based method) hard-navigates the buyer to the
    // bank and back to `/checkout/return`, which resets this module's `warmed` guard
    // and makes the guest that the engine minted moments ago look like a leftover.
    // Reaping it signs the buyer out mid-payment — the engine then mints a SECOND
    // guest, so the return finalizes against an identity that doesn't own the order
    // being paid, and the buyer loses access to what they just bought. The whole
    // standalone `/checkout` tree opts in (return → success, and error → retry are
    // all hard navigations that re-run this).
    //
    // `logout` is dynamic-imported to keep `firebase/auth` off the first-load bundle.
    if (keepGuestSession) {
      // Still leave the cookie PRESENT: a guest decodes as anonymous server-side
      // either way, and `n:1` is what keeps `trySessionRefresh` from redirecting.
      try {
        await markSessionNone();
      } catch (error) {
        console.error(
          '[warmSessionCookie] marking the in-flight guest failed:',
          error,
        );
      }
      return;
    }
    try {
      const { logout } = await import('@ps/functions/auth/logout');
      await logout();
    } catch (error) {
      console.error('[warmSessionCookie] guest cleanup failed:', error);
    }
    return;
  }

  // A user who verified a code but never finished signup (tab closed, app
  // killed, before `CreateUserV2`) is restored here as signed in with no
  // profile. Minting them a cookie lets every gate that only asks "signed in,
  // not a guest" wave them through — Subscribe then dies in checkout as
  // `account_load_failed`. `PostLoginGate` clears this state on mount, but
  // only when the login modal opens, which these paths never do.
  if (!keepProfilelessSession && !(await hasProfile(user.uid))) {
    try {
      const { logout } = await import('@ps/functions/auth/logout');
      await logout();
    } catch (error) {
      console.error('[warmSessionCookie] profileless cleanup failed:', error);
    }
    return;
  }

  try {
    const idToken = await user.getIdToken(/* forceRefresh */ false);
    await postSessionCookie(idToken);
  } catch (error) {
    console.error('[warmSessionCookie] postSessionCookie failed:', error);
  }
}
